import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";

import { zinc } from "@/ui/tokens";

/** A field a picker offers: its column id, its header text, and the section it sits under. */
export type FieldOption = { key: string; label: string; group: string };

const optionSx = (selected: boolean) =>
  ({
    width: "100%",
    justifyContent: "flex-start",
    gap: 0.75,
    px: 1,
    py: 0.375,
    borderRadius: 1,
    fontSize: "0.8125rem",
    textAlign: "left",
    color: selected ? "white" : zinc[200],
    "&:hover, &[data-highlighted=true]": { bgcolor: zinc[700], color: "white" },
  }) as const;

/**
 * A field chooser for the Display popover: reads like a select, opens to a search box over every field it offers, in
 * sections (the driver's fields, one message's, the tags). The list is the whole field space, columns shown or hidden
 * and fields that are never columns, so it is searched rather than scrolled: typing narrows it, Enter takes the
 * highlighted entry, the arrows move the highlight. "None" leads the list while nothing is typed.
 */
export default function DispatchFieldPicker({
  testId,
  ariaLabel,
  value,
  valueLabel,
  options,
  noneLabel = "None",
  onChange,
}: {
  testId: string;
  ariaLabel: string;
  /** The chosen field's column id, or `null` for none. */
  value: string | null;
  /** What the closed picker reads when a field is chosen; the option's label when it is one of `options`. */
  valueLabel?: string;
  options: readonly FieldOption[];
  noneLabel?: string;
  onChange: (key: string | null) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const term = query.trim().toLowerCase();
  const shown = useMemo(
    () => (term === "" ? options : options.filter((option) => option.label.toLowerCase().includes(term))),
    [options, term],
  );
  // The flat list the highlight walks: "None" first while nothing is typed, then the sections' entries in order.
  const entries = useMemo<(FieldOption | null)[]>(() => (term === "" ? [null, ...shown] : [...shown]), [term, shown]);
  const groups = useMemo(() => {
    const byGroup = new Map<string, FieldOption[]>();
    for (const option of shown) {
      const list = byGroup.get(option.group);
      if (list) list.push(option);
      else byGroup.set(option.group, [option]);
    }
    return [...byGroup.entries()];
  }, [shown]);

  const chosen = options.find((option) => option.key === value);
  const shownLabel = value == null ? noneLabel : (chosen?.label ?? valueLabel ?? value);
  const close = () => {
    setOpen(false);
    setQuery("");
    setHighlighted(0);
  };
  const pick = (key: string | null) => {
    onChange(key);
    close();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlighted((index) => Math.min(entries.length - 1, Math.max(0, index + step)));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const entry = entries[Math.min(highlighted, entries.length - 1)];
      if (entry !== undefined) pick(entry === null ? null : entry.key);
    }
  };
  const positions = new Map(entries.map((entry, index) => [entry?.key ?? null, index]));
  const optionRow = (option: FieldOption | null) => {
    const key = option?.key ?? null;
    const position = positions.get(key) ?? -1;
    const selected = key === value;
    return (
      <ButtonBase
        key={key ?? "__none"}
        role="option"
        aria-selected={selected}
        data-highlighted={position === highlighted}
        onMouseEnter={() => setHighlighted(position)}
        onClick={() => pick(key)}
        sx={optionSx(selected)}
      >
        <Box component="span" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {option?.label ?? noneLabel}
        </Box>
        {selected && <CheckIcon sx={{ fontSize: 14, color: "var(--color-brand-primary-400)" }} />}
      </ButtonBase>
    );
  };

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={testId}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        sx={{
          flex: 1,
          minWidth: 0,
          height: 30,
          pl: 1.25,
          pr: 0.5,
          borderRadius: 1,
          justifyContent: "space-between",
          bgcolor: zinc[700],
          color: value == null ? zinc[400] : zinc[100],
          fontSize: "0.8125rem",
          "&:hover": { bgcolor: zinc[600], color: "white" },
        }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {shownLabel}
        </Box>
        <ArrowDropDownIcon sx={{ fontSize: 20, color: zinc[400], flex: "none" }} />
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 260,
              bgcolor: zinc[800],
              color: "white",
              border: `1px solid ${zinc[600]}`,
              p: 0.75,
              mt: 0.25,
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: 30,
            px: 1,
            mb: 0.5,
            borderRadius: 1,
            bgcolor: zinc[700],
            fontSize: "0.8125rem",
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: zinc[400] }} />
          <InputBase
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setHighlighted(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Find a field"
            inputProps={{ "aria-label": "Find a field" }}
            sx={{ flex: 1, color: "white", fontSize: "inherit", "& input": { p: 0 } }}
          />
        </Box>
        <Box role="listbox" aria-label={ariaLabel} sx={{ maxHeight: 320, overflowY: "auto" }}>
          {term === "" && optionRow(null)}
          {groups.map(([group, fields]) => (
            <Box key={group}>
              <Typography
                sx={{
                  px: 1,
                  pt: 0.75,
                  pb: 0.25,
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: zinc[500],
                }}
              >
                {group}
              </Typography>
              {fields.map(optionRow)}
            </Box>
          ))}
          {entries.length === 0 && (
            <Typography sx={{ px: 1, py: 0.75, fontSize: "0.8125rem", color: zinc[500] }}>No field matches</Typography>
          )}
        </Box>
      </Popover>
    </>
  );
}
